import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Typography, Autocomplete, TextField, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Portal, FormControlLabel, Checkbox, Chip } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const HwQuestionCropTool = ({
    textbook = null,
    page = 0,
    numPages = null,
    showTextbookSelector = false,
    showPageSelector = false,
    textbooks = [],
    pdfUrl = null,
    onExtract,
    onError,
    compact = false,
    externalPdfContainerRef = null, // Use external PDF viewer instead of rendering our own
    useExternalPdf = false // Flag to indicate we should use external PDF
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [chosenTextbook, setChosenTextbook] = useState(textbook);
    const [selectedPage, setSelectedPage] = useState(page);
    const [pdfNumPages, setPdfNumPages] = useState(numPages);
    const [pdfLoading, setPdfLoading] = useState(false);

    // Cropping states
    const [isCropping, setIsCropping] = useState(false);
    const [crop, setCrop] = useState({ unit: '%', width: 30, aspect: undefined });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [croppedImages, setCroppedImages] = useState([]); // Array of {page, question_image, diagram_image?, option_images: [], cropArea}

    const [extracting, setExtracting] = useState(false);
    const [pageImageUrl, setPageImageUrl] = useState(null); // Data URL of current page for cropping
    const [convertingPage, setConvertingPage] = useState(false);
    const imgRef = useRef(null);
    const pdfContainerRef = useRef(null);

    // New states for multi-crop workflow
    const [cropType, setCropType] = useState('question'); // 'question', 'diagram', or 'option'
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null); // Index of question to add diagram/option to
    const [showDiagramPrompt, setShowDiagramPrompt] = useState(false);
    const [showCropChoiceDialog, setShowCropChoiceDialog] = useState(false); // Show choice: diagram, option, or skip

    // AI answer solving toggle
    const [useReasoningForAnswers, setUseReasoningForAnswers] = useState(false);

    // Memoize pdf.js options to prevent re-triggering Document load on every render.
    // disableAutoFetch stops pdf.js from pre-downloading all pages after the first loads.
    const pdfOptions = React.useMemo(() => ({
        disableAutoFetch: true,
        disableStream: false,
    }), []);

    // Multi-part question cropping states
    const [currentPartNumber, setCurrentPartNumber] = useState(1);
    const [isMultiPartMode, setIsMultiPartMode] = useState(false);

    // Shared image for multiple questions
    const [sharedImage, setSharedImage] = useState(null); // { base64, page, cropArea }

    // Parent-child relationship states
    const [showParentSelectionDialog, setShowParentSelectionDialog] = useState(false);
    const [pendingChildIndex, setPendingChildIndex] = useState(null);

    // Update state when props change
    useEffect(() => {
        if (textbook) {
            setChosenTextbook(textbook);
        }
    }, [textbook]);

    useEffect(() => {
        if (page) {
            setSelectedPage(page);
        }
    }, [page]);

    useEffect(() => {
        if (numPages) {
            setPdfNumPages(numPages);
        }
    }, [numPages]);

    // Store previous page to detect changes during cropping
    const prevPageRef = useRef(null);

    // Re-capture page image when page changes during active cropping
    useEffect(() => {
        const currentPage = selectedPage || page;

        // Only re-capture if already cropping and page actually changed
        if (isCropping && prevPageRef.current !== null && prevPageRef.current !== currentPage && currentPage > 0) {
            setPageImageUrl(null);
            setCrop({ unit: '%', width: 30, aspect: undefined });
            setCompletedCrop(null);

            const timer = setTimeout(() => {
                convertPageToImage();
            }, 300);
            return () => clearTimeout(timer);
        }

        prevPageRef.current = currentPage;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPage, page, isCropping]);

    // Determine effective values
    const effectivePdfUrl = pdfUrl || chosenTextbook?.file_url;
    const effectiveTextbook = chosenTextbook || textbook;
    const effectivePage = selectedPage || page;
    const effectiveNumPages = pdfNumPages || numPages;

    const onDocumentLoadSuccess = ({ numPages }) => {
        setPdfNumPages(numPages);
        setPdfLoading(false);
    };

    const onPageLoadSuccess = () => {
        // Page loaded successfully
    };

    const convertPageToImage = async () => {
        if (!effectivePage || effectivePage === 0) {
            onError('Please select a page');
            return;
        }

        setConvertingPage(true);
        try {
            // Use external PDF container if provided, otherwise use internal
            const containerRef = useExternalPdf && externalPdfContainerRef ? externalPdfContainerRef : pdfContainerRef;

            // Small delay to ensure canvas is fully rendered
            await new Promise(resolve => setTimeout(resolve, 100));

            // Get the canvas element from the visible PDF container
            // Try multiple selectors to find the canvas
            let canvas = containerRef.current?.querySelector('canvas');
            if (!canvas) {
                canvas = containerRef.current?.querySelector('.react-pdf__Page__canvas');
            }
            if (!canvas) {
                canvas = containerRef.current?.querySelector('.react-pdf__Page canvas');
            }

            if (!canvas) {
                console.error('Canvas not found. Container:', containerRef.current);
                console.error('Looking for canvas in:', useExternalPdf ? 'external PDF' : 'internal PDF');
                throw new Error('PDF canvas not found. Please wait for the PDF to fully load.');
            }

            console.log('Canvas found:', canvas);

            // Convert canvas to data URL
            const imageDataUrl = canvas.toDataURL('image/png');
            setPageImageUrl(imageDataUrl);
            if (!isCropping) {
                setIsCropping(true);
            }
        } catch (error) {
            console.error('Error converting page to image:', error);
            onError(error.message || 'Failed to prepare page for cropping');
        } finally {
            setConvertingPage(false);
        }
    };

    const saveCrop = async () => {
        if (!completedCrop || !imgRef.current || !effectiveTextbook || effectivePage === 0) {
            onError('Please select a textbook, page, and crop area');
            return;
        }

        try {
            const image = imgRef.current;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;

            canvas.width = completedCrop.width;
            canvas.height = completedCrop.height;

            ctx.drawImage(
                image,
                completedCrop.x * scaleX,
                completedCrop.y * scaleY,
                completedCrop.width * scaleX,
                completedCrop.height * scaleY,
                0,
                0,
                completedCrop.width,
                completedCrop.height
            );

            // Convert to base64 - use WebP for diagrams and options, PNG for questions
            const imageFormat = (cropType === 'diagram' || cropType === 'option') ? 'image/webp' : 'image/png';
            const quality = (cropType === 'diagram' || cropType === 'option') ? 0.95 : undefined;
            const base64Image = canvas.toDataURL(imageFormat, quality).split(',')[1];

            if (cropType === 'question') {
                if (isMultiPartMode && currentQuestionIndex !== null) {
                    // Adding additional part to existing question
                    const updatedImages = [...croppedImages];
                    const currentQuestion = updatedImages[currentQuestionIndex];

                    // Ensure arrays exist (backward compat)
                    if (!currentQuestion.question_images) {
                        currentQuestion.question_images = [currentQuestion.question_image];
                        currentQuestion.question_pages = [currentQuestion.page];
                    }

                    currentQuestion.question_images.push(base64Image);
                    currentQuestion.question_pages.push(effectivePage);
                    setCroppedImages(updatedImages);
                    setCurrentPartNumber(prev => prev + 1);

                    // Ask again for more crops
                    setShowCropChoiceDialog(true);
                } else {
                    // Save new question crop (first part)
                    const newIndex = croppedImages.length;
                    setCroppedImages([
                        ...croppedImages,
                        {
                            page: effectivePage,
                            question_images: [base64Image],
                            question_pages: [effectivePage],
                            question_image: base64Image, // Keep for backward compatibility
                            diagram_image: null,
                            diagram_page: null,
                            option_images: [],
                            option_pages: [],
                            cropArea: completedCrop,
                            // Parent-child relationship fields
                            parent_index: null,        // Index of parent in croppedImages array (null = standalone)
                            is_parent: false,          // True if this question has children
                            children_indices: [],      // Array of child indices
                        },
                    ]);

                    // Ask if user wants to add more parts, diagram, or option image
                    setCurrentQuestionIndex(newIndex);
                    setCurrentPartNumber(1);
                    setShowCropChoiceDialog(true);
                }
            } else if (cropType === 'diagram') {
                // Add diagram to existing question
                const updatedImages = [...croppedImages];
                updatedImages[currentQuestionIndex].diagram_image = base64Image;
                updatedImages[currentQuestionIndex].diagram_page = effectivePage;
                setCroppedImages(updatedImages);

                // Ask again for more crops
                setShowCropChoiceDialog(true);
            } else if (cropType === 'option') {
                // Add option image to existing question
                const updatedImages = [...croppedImages];
                updatedImages[currentQuestionIndex].option_images.push(base64Image);
                updatedImages[currentQuestionIndex].option_pages = updatedImages[currentQuestionIndex].option_pages || [];
                updatedImages[currentQuestionIndex].option_pages.push(effectivePage);
                setCroppedImages(updatedImages);

                // Ask again for more crops
                setShowCropChoiceDialog(true);
            }

            // Reset crop state
            setIsCropping(false);
            setPageImageUrl(null);
            setCrop({ unit: '%', width: 30, aspect: undefined });
            setCompletedCrop(null);
        } catch (error) {
            console.error('Error saving crop:', error);
            onError('Failed to save crop: ' + error.message);
        }
    };

    const deleteCrop = (index) => {
        // First, handle parent-child relationships
        const itemToDelete = croppedImages[index];
        if (!itemToDelete) return; // Defensive check

        let updatedImages = [...croppedImages];

        // If deleting a parent, clear children's parent_index
        if (itemToDelete.is_parent && itemToDelete.children_indices) {
            itemToDelete.children_indices.forEach(childIdx => {
                if (updatedImages[childIdx]) {
                    updatedImages[childIdx].parent_index = null;
                }
            });
        }

        // If deleting a child, remove from parent's children_indices
        if (itemToDelete.parent_index !== null && itemToDelete.parent_index !== undefined) {
            const parentIdx = itemToDelete.parent_index;
            if (updatedImages[parentIdx]) {
                updatedImages[parentIdx].children_indices = updatedImages[parentIdx].children_indices.filter(
                    idx => idx !== index
                );
                if (updatedImages[parentIdx].children_indices.length === 0) {
                    updatedImages[parentIdx].is_parent = false;
                }
            }
        }

        // Remove the item
        updatedImages = updatedImages.filter((_, i) => i !== index);

        // Update all indices that are greater than the deleted index
        updatedImages = updatedImages.map(item => {
            const updated = { ...item };
            // Update parent_index if it pointed to an index after the deleted one
            if (updated.parent_index !== null && updated.parent_index !== undefined && updated.parent_index > index) {
                updated.parent_index = updated.parent_index - 1;
            }
            // Update children_indices
            if (updated.children_indices && updated.children_indices.length > 0) {
                updated.children_indices = updated.children_indices
                    .filter(idx => idx !== index) // Remove the deleted index
                    .map(idx => idx > index ? idx - 1 : idx); // Shift indices down
            }
            return updated;
        });

        setCroppedImages(updatedImages);

        // Reset states if deleting the current question being edited
        if (currentQuestionIndex === index) {
            setCurrentQuestionIndex(null);
            setIsMultiPartMode(false);
            setCurrentPartNumber(1);
            setCropType('question');
        } else if (currentQuestionIndex !== null && currentQuestionIndex > index) {
            // Adjust index if deleting a question before the current one
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleAddDiagram = (questionIndex) => {
        setCurrentQuestionIndex(questionIndex);
        setCropType('diagram');
        setShowDiagramPrompt(false);
        setShowCropChoiceDialog(false);
        convertPageToImage(); // Start cropping for diagram
    };

    const handleAddOptionImage = () => {
        setCropType('option');
        setShowCropChoiceDialog(false);
        convertPageToImage(); // Start cropping for option
    };

    const handleSkipAdditionalCrops = () => {
        setShowDiagramPrompt(false);
        setShowCropChoiceDialog(false);
        setCurrentQuestionIndex(null);
        setCropType('question');
        setIsMultiPartMode(false);
        setCurrentPartNumber(1);
    };

    const handleAddQuestionPart = () => {
        // Continue adding parts to current question
        setIsMultiPartMode(true);
        setCropType('question');
        setShowCropChoiceDialog(false);
        convertPageToImage();
    };

    const handleFinishQuestion = () => {
        // Done with current question, reset for new one
        setIsMultiPartMode(false);
        setCurrentPartNumber(1);
        setCurrentQuestionIndex(null);
        setCropType('question');
        setShowCropChoiceDialog(false);
    };

    // Parent-child relationship handlers
    const handleMarkAsChild = () => {
        setShowCropChoiceDialog(false);
        setShowParentSelectionDialog(true);
        setPendingChildIndex(currentQuestionIndex);
    };

    const handleSelectParent = (parentIndex) => {
        const updated = [...croppedImages];

        // Set child's parent reference
        updated[pendingChildIndex].parent_index = parentIndex;

        // Update parent to mark it has children
        if (!updated[parentIndex].children_indices) {
            updated[parentIndex].children_indices = [];
        }
        updated[parentIndex].children_indices.push(pendingChildIndex);
        updated[parentIndex].is_parent = true;

        setCroppedImages(updated);
        setShowParentSelectionDialog(false);
        setPendingChildIndex(null);
        handleFinishQuestion();
    };

    const handleCancelParentSelection = () => {
        setShowParentSelectionDialog(false);
        setPendingChildIndex(null);
        setShowCropChoiceDialog(true); // Go back to crop choice dialog
    };

    const handleRemoveParent = (childIndex) => {
        const updated = [...croppedImages];
        const parentIndex = updated[childIndex].parent_index;

        // Remove from parent's children list
        if (parentIndex !== null && updated[parentIndex]) {
            updated[parentIndex].children_indices = updated[parentIndex].children_indices.filter(
                idx => idx !== childIndex
            );
            if (updated[parentIndex].children_indices.length === 0) {
                updated[parentIndex].is_parent = false;
            }
        }

        // Clear child's parent reference
        updated[childIndex].parent_index = null;

        setCroppedImages(updated);
    };

    // Shared image handler
    const handleUseAsSharedImage = (questionIndex, type) => {
        const cropItem = croppedImages[questionIndex];
        setSharedImage({
            base64: cropItem.question_images?.[0] || cropItem.question_image,
            page: cropItem.page,
            cropArea: cropItem.cropArea,
            type, // 'content' or 'diagram'
        });
        // Remove from croppedImages — it's now a shared context, not an individual question
        deleteCrop(questionIndex);
        setShowCropChoiceDialog(false);
        setCurrentQuestionIndex(null);
        setIsMultiPartMode(false);
        setCurrentPartNumber(1);
    };

    const handleAddPartToExistingQuestion = (questionIndex) => {
        // Called from "Add Part" button on existing question card
        const existingParts = croppedImages[questionIndex].question_images?.length || 1;
        setCurrentQuestionIndex(questionIndex);
        setCurrentPartNumber(existingParts); // Set to current count, saveCrop will increment after saving
        setIsMultiPartMode(true);
        setCropType('question');
        convertPageToImage();
    };

    const handleExtract = async () => {
        if (!effectiveTextbook) {
            onError('Please select a textbook');
            return;
        }

        if (croppedImages.length === 0) {
            onError('Please crop at least one question');
            return;
        }

        setExtracting(true);

        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/extract/crop`,
                {
                    textbook_id: effectiveTextbook.id,
                    cropped_images: croppedImages,
                    use_reasoning_for_answers: useReasoningForAnswers,
                    shared_image: sharedImage?.base64 || null,
                    shared_image_page: sharedImage?.page || null,
                    shared_image_type: sharedImage?.type || null,
                },
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            // Call onExtract with the extraction job ID
            if (onExtract) {
                onExtract(response.data.extraction_job_id);
            }
        } catch (error) {
            console.error('Extract error:', error);
            onError(error.response?.data?.error || 'Failed to start extraction');
        } finally {
            setExtracting(false);
        }
    };

    return (
        <Box>
            {/* Cropping Overlay (appears on top of external PDF) */}
            {useExternalPdf && isCropping && pageImageUrl && externalPdfContainerRef?.current && (
                <Portal container={externalPdfContainerRef.current}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            zIndex: 1000,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            overflow: 'auto',
                            padding: '20px',
                        }}
                    >
                        <ReactCrop
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={(c) => setCompletedCrop(c)}
                        >
                            <img
                                ref={imgRef}
                                src={pageImageUrl}
                                alt="PDF Page"
                                style={{ maxWidth: '100%', height: 'auto' }}
                            />
                        </ReactCrop>
                    </Box>
                </Portal>
            )}

            <Typography variant="h5" fontWeight="bold" mb={3}>
                Crop Questions from PDF
            </Typography>

            <Box display="flex" gap={3} mb={3} flexWrap="wrap">
                {/* Textbook Selector (conditional) */}
                {showTextbookSelector && (
                    <Autocomplete
                        disablePortal
                        options={textbooks}
                        sx={{ width: 300 }}
                        value={chosenTextbook}
                        renderInput={(params) => <TextField {...params} label="Choose Textbook" />}
                        onChange={(_, newValue) => {
                            setChosenTextbook(newValue);
                            setSelectedPage(0);
                            setPdfNumPages(null);
                            setCroppedImages([]);
                            setSharedImage(null);
                            if (newValue) {
                                setPdfLoading(true);
                            }
                        }}
                    />
                )}

                {/* Hidden PDF loader to get page count (if showTextbookSelector) */}
                {showTextbookSelector && chosenTextbook && (
                    <Box sx={{ display: 'none' }}>
                        <Document file={chosenTextbook.file_url} onLoadSuccess={onDocumentLoadSuccess}>
                            <Page pageNumber={1} />
                        </Document>
                    </Box>
                )}

                {/* Page Selector (conditional) */}
                {showPageSelector && (
                    <FormControl disabled={!effectiveTextbook || pdfLoading} sx={{ minWidth: 120 }}>
                        <InputLabel>Choose Page</InputLabel>
                        <Select
                            value={effectivePage}
                            label="Page"
                            onChange={(event) => {
                                setSelectedPage(event.target.value);
                                // Allow page navigation during cropping - useEffect will handle re-capture
                            }}
                        >
                            <MenuItem value={0}>
                                <em>None</em>
                            </MenuItem>
                            {Array(effectiveNumPages)
                                .fill(0)
                                .map((_, index) => (
                                    <MenuItem key={index} value={index + 1}>
                                        {index + 1}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            {effectiveTextbook && effectivePage > 0 && (
                <Box>
                    <Box display="flex" gap={2} mb={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                if (isCropping) {
                                    // Cancel cropping
                                    setIsCropping(false);
                                    setPageImageUrl(null);
                                    setCrop({ unit: '%', width: 30, aspect: undefined });
                                    setCompletedCrop(null);
                                    setCropType('question');
                                    setShowDiagramPrompt(false);
                                    setIsMultiPartMode(false);
                                    setCurrentPartNumber(1);
                                } else {
                                    // Start cropping
                                    convertPageToImage();
                                }
                            }}
                            disabled={extracting || convertingPage}
                        >
                            {convertingPage
                                ? 'Preparing...'
                                : isCropping
                                    ? 'Cancel Crop'
                                    : cropType === 'diagram'
                                        ? 'Crop Diagram'
                                        : cropType === 'option'
                                            ? 'Crop Option Image'
                                            : isMultiPartMode
                                                ? `Crop Question Part ${currentPartNumber + 1}`
                                                : 'Crop Question'}
                        </Button>
                        {isCropping && (
                            <Button variant="contained" color="success" onClick={saveCrop}>
                                Save Crop
                            </Button>
                        )}
                    </Box>

                    {/* Show hint when using external PDF and not cropping */}
                    {useExternalPdf && !isCropping && (
                        <Box
                            sx={{
                                p: 3,
                                border: `2px dashed ${colors.primary[400]}`,
                                borderRadius: '8px',
                                textAlign: 'center',
                                backgroundColor: colors.primary[700],
                            }}
                        >
                            <Typography variant="h6" mb={1}>
                                Click "Crop Question" to start cropping
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                The cropping interface will appear on the PDF viewer on the left
                            </Typography>
                        </Box>
                    )}

                    {/* Only show PDF viewer if NOT using external PDF */}
                    {!useExternalPdf && (
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                minHeight: compact ? '600px' : '800px',
                                border: `2px solid ${colors.primary[400]}`,
                                borderRadius: '8px',
                                overflow: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                padding: '20px',
                            }}
                        >
                            {isCropping && pageImageUrl ? (
                                <ReactCrop
                                    crop={crop}
                                    onChange={(c) => setCrop(c)}
                                    onComplete={(c) => setCompletedCrop(c)}
                                >
                                    <img
                                        ref={imgRef}
                                        src={pageImageUrl}
                                        alt="PDF Page"
                                        style={{ maxWidth: '800px', height: 'auto' }}
                                    />
                                </ReactCrop>
                            ) : (
                                <Box ref={pdfContainerRef} sx={{ maxWidth: '800px' }}>
                                    <Document
                                        file={effectivePdfUrl}
                                        options={pdfOptions}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                        loading={<Typography>Loading PDF...</Typography>}
                                    >
                                        <Page
                                            pageNumber={effectivePage}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                            onLoadSuccess={onPageLoadSuccess}
                                            scale={2.0}
                                        />
                                    </Document>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Show instructions when using external PDF and cropping */}
                    {useExternalPdf && isCropping && (
                        <Box mt={2}>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                                ✓ Cropping mode active - Use the PDF on the left to select the area
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mt={1}>
                                Drag the corner handles to resize, or drag the center to move the crop area
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Shared Image Banner */}
            {sharedImage && (
                <Box
                    mt={3}
                    p={2}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    sx={{
                        border: `2px solid ${colors.blueAccent[500]}`,
                        borderRadius: '8px',
                        backgroundColor: colors.primary[700],
                    }}
                >
                    <img
                        src={`data:image/png;base64,${sharedImage.base64}`}
                        alt="Shared context"
                        style={{ width: '80px', height: 'auto', borderRadius: '4px', border: `2px solid ${colors.blueAccent[400]}` }}
                    />
                    <Box flex={1}>
                        <Typography variant="subtitle2" fontWeight="bold" color={sharedImage.type === 'diagram' ? colors.greenAccent[300] : colors.blueAccent[300]}>
                            Shared {sharedImage.type === 'diagram' ? 'Diagram' : 'Content'} Active (Page {sharedImage.page})
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {sharedImage.type === 'diagram'
                                ? 'All new questions will include this diagram image'
                                : 'All new questions will include OCR text from this image'}
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => setSharedImage(null)}
                    >
                        Clear
                    </Button>
                </Box>
            )}

            {/* Cropped Images List */}
            {croppedImages.length > 0 && (
                <Box mt={4}>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        Cropped Questions ({croppedImages.length})
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        {croppedImages.map((cropItem, index) => (
                            <Box
                                key={index}
                                sx={{
                                    position: 'relative',
                                    border: `1px solid ${colors.primary[400]}`,
                                    borderRadius: '4px',
                                    padding: '8px',
                                    minWidth: '220px',
                                }}
                            >
                                {/* Header with Badge */}
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="caption" fontWeight="bold">
                                        Question {index + 1} - Page {cropItem.page}
                                    </Typography>
                                    <Box display="flex" gap={0.5}>
                                        {(cropItem.question_images?.length || 1) > 1 && (
                                            <Chip
                                                label={`${cropItem.question_images.length} parts`}
                                                size="small"
                                                color="primary"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                        )}
                                        {/* Shared image indicator */}
                                        {sharedImage && (
                                            <Chip
                                                label="Shared Img"
                                                size="small"
                                                color="secondary"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                        )}
                                        {/* Parent indicator */}
                                        {cropItem.is_parent && (
                                            <Chip
                                                label={`Parent (${cropItem.children_indices?.length || 0})`}
                                                size="small"
                                                color="success"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                        )}
                                        {/* Child indicator */}
                                        {(cropItem.parent_index !== null && cropItem.parent_index !== undefined) && (
                                            <Chip
                                                label={`Child of Q${cropItem.parent_index + 1}`}
                                                size="small"
                                                color="warning"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                                onDelete={() => handleRemoveParent(index)}
                                            />
                                        )}
                                    </Box>
                                </Box>

                                {/* Question Images - Supports Multiple Parts */}
                                <Box mt={1}>
                                    <Typography variant="caption" color="textSecondary">
                                        Question {(cropItem.question_images?.length || 1) > 1 ? 'Parts' : 'Text'}:
                                    </Typography>
                                    <Box display="flex" flexDirection="column" gap={1} mt={0.5}>
                                        {(cropItem.question_images || [cropItem.question_image || cropItem.image]).map((img, partIdx) => (
                                            <Box key={partIdx} sx={{ position: 'relative' }}>
                                                {(cropItem.question_images?.length || 1) > 1 && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 4,
                                                            left: 4,
                                                            bgcolor: 'rgba(0,0,0,0.7)',
                                                            color: 'white',
                                                            px: 0.5,
                                                            borderRadius: 1,
                                                            fontSize: '0.65rem',
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        Part {partIdx + 1}
                                                        {cropItem.question_pages?.[partIdx] && cropItem.question_pages[partIdx] !== cropItem.page &&
                                                            ` (p.${cropItem.question_pages[partIdx]})`}
                                                    </Typography>
                                                )}
                                                <img
                                                    src={`data:image/png;base64,${img}`}
                                                    alt={`Question ${index + 1}${(cropItem.question_images?.length || 1) > 1 ? ` Part ${partIdx + 1}` : ''}`}
                                                    style={{ width: '200px', height: 'auto', display: 'block' }}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>

                                {/* Add Part Button (if less than 5 parts) */}
                                {(cropItem.question_images?.length || 1) < 5 && (
                                    <Button
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        onClick={() => handleAddPartToExistingQuestion(index)}
                                        sx={{ mt: 1, width: '100%' }}
                                        disabled={isCropping}
                                    >
                                        + Add Part ({cropItem.question_images?.length || 1}/5)
                                    </Button>
                                )}

                                {/* Diagram Image if exists */}
                                {cropItem.diagram_image && (
                                    <Box mt={1}>
                                        <Typography variant="caption" color="textSecondary">
                                            Diagram{cropItem.diagram_page && cropItem.diagram_page !== cropItem.page ? ` (from Page ${cropItem.diagram_page})` : ''}:
                                        </Typography>
                                        <img
                                            src={`data:image/webp;base64,${cropItem.diagram_image}`}
                                            alt={`Diagram ${index + 1}`}
                                            style={{ width: '200px', height: 'auto', display: 'block', marginTop: '4px', border: `2px solid ${colors.greenAccent[500]}` }}
                                        />
                                    </Box>
                                )}

                                {/* Option Images if exist */}
                                {cropItem.option_images && cropItem.option_images.length > 0 && (
                                    <Box mt={1}>
                                        <Typography variant="caption" color="textSecondary">
                                            Option Images ({cropItem.option_images.length}):
                                        </Typography>
                                        <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                                            {cropItem.option_images.map((optionImage, optIdx) => (
                                                <Box key={optIdx} sx={{ textAlign: 'center' }}>
                                                    <img
                                                        src={`data:image/webp;base64,${optionImage}`}
                                                        alt={`Option ${optIdx + 1}`}
                                                        style={{ width: '90px', height: 'auto', display: 'block', border: `2px solid ${colors.blueAccent[500]}` }}
                                                    />
                                                    {cropItem.option_pages?.[optIdx] && cropItem.option_pages[optIdx] !== cropItem.page && (
                                                        <Typography variant="caption" sx={{ fontSize: '10px', color: colors.blueAccent[400] }}>
                                                            Page {cropItem.option_pages[optIdx]}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* Add Diagram button if no diagram yet */}
                                {!cropItem.diagram_image && (
                                    <Button
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        onClick={() => handleAddDiagram(index)}
                                        sx={{
                                            mt: 1,
                                            width: '100%',
                                            '&.Mui-disabled': {
                                                color: colors.grey[500],
                                                borderColor: colors.grey[600],
                                            }
                                        }}
                                        disabled={isCropping}
                                    >
                                        Add Diagram
                                    </Button>
                                )}

                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => deleteCrop(index)}
                                    sx={{ mt: 1, width: '100%' }}
                                >
                                    Delete Question
                                </Button>
                            </Box>
                        ))}
                    </Box>

                    <Box mt={3}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useReasoningForAnswers}
                                    onChange={(e) => setUseReasoningForAnswers(e.target.checked)}
                                />
                            }
                            label="Use reasoning model for answers (slower but more accurate for math/science)"
                            sx={{ mb: 2, display: 'block' }}
                        />
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            onClick={handleExtract}
                            disabled={extracting}
                        >
                            {extracting ? 'Starting Extraction...' : `Extract ${croppedImages.length} Questions`}
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Diagram Prompt Dialog (legacy - kept for backward compatibility) */}
            <Dialog open={showDiagramPrompt} onClose={handleSkipAdditionalCrops}>
                <DialogTitle>Add Diagram?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Does this question have a diagram or image that should be displayed alongside it?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSkipAdditionalCrops} color="primary">
                        No, Skip
                    </Button>
                    <Button onClick={() => handleAddDiagram(currentQuestionIndex)} color="secondary" variant="contained" autoFocus>
                        Yes, Add Diagram
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Crop Choice Dialog - Choose what to crop next */}
            <Dialog open={showCropChoiceDialog} onClose={handleFinishQuestion} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {isMultiPartMode && currentPartNumber > 1
                        ? `Part ${currentPartNumber} Saved!`
                        : 'Question Crop Saved!'}
                </DialogTitle>
                <DialogContent>
                    {/* Preview of current parts */}
                    {currentQuestionIndex !== null && croppedImages[currentQuestionIndex] && (
                        <Box mb={2}>
                            <Typography variant="body2" color="textSecondary" mb={1}>
                                Question {currentQuestionIndex + 1} now has{' '}
                                {croppedImages[currentQuestionIndex].question_images?.length || 1} part(s)
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                {(croppedImages[currentQuestionIndex].question_images ||
                                    [croppedImages[currentQuestionIndex].question_image]).map((img, idx) => (
                                    <Box key={idx} sx={{ textAlign: 'center', position: 'relative' }}>
                                        <img
                                            src={`data:image/png;base64,${img}`}
                                            alt={`Part ${idx + 1}`}
                                            style={{
                                                width: 80,
                                                height: 'auto',
                                                border: `2px solid ${colors.primary[400]}`,
                                                borderRadius: 4
                                            }}
                                        />
                                        <Typography variant="caption" display="block">
                                            Part {idx + 1}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                    <DialogContentText>
                        What would you like to do next?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2, alignItems: 'stretch' }}>
                    {/* Primary: Add Another Part */}
                    <Button
                        onClick={handleAddQuestionPart}
                        color="success"
                        variant="contained"
                        fullWidth
                        disabled={(croppedImages[currentQuestionIndex]?.question_images?.length || 1) >= 5}
                    >
                        + Add Another Part of This Question
                        {(croppedImages[currentQuestionIndex]?.question_images?.length || 1) >= 5 && ' (Max 5)'}
                    </Button>

                    {/* Secondary: Diagram & Options */}
                    <Box display="flex" gap={1} width="100%">
                        <Button
                            onClick={handleAddOptionImage}
                            color="info"
                            variant="outlined"
                            sx={{ flex: 1 }}
                        >
                            Add Option Image
                        </Button>
                        <Button
                            onClick={() => handleAddDiagram(currentQuestionIndex)}
                            color="success"
                            variant="outlined"
                            sx={{ flex: 1 }}
                            disabled={croppedImages[currentQuestionIndex]?.diagram_image !== null}
                        >
                            Add Diagram
                        </Button>
                    </Box>

                    {/* Mark as Child - only if there are other questions that can be parents */}
                    {croppedImages.length > 1 && currentQuestionIndex > 0 && (
                        <Button
                            onClick={handleMarkAsChild}
                            color="warning"
                            variant="outlined"
                            fullWidth
                            disabled={
                                // Disable if this question already has children (can't be both parent and child)
                                croppedImages[currentQuestionIndex]?.is_parent
                            }
                        >
                            Mark as Child of Another Question
                        </Button>
                    )}

                    {/* Use as Shared Image for multiple questions */}
                    <Box display="flex" gap={1} width="100%">
                        <Button
                            onClick={() => handleUseAsSharedImage(currentQuestionIndex, 'content')}
                            variant="outlined"
                            sx={{
                                flex: 1,
                                color: colors.blueAccent[300],
                                borderColor: colors.blueAccent[400],
                                '&:hover': {
                                    borderColor: colors.blueAccent[300],
                                    backgroundColor: `${colors.blueAccent[400]}22`,
                                },
                                '&.Mui-disabled': {
                                    color: colors.grey[500],
                                    borderColor: colors.grey[600],
                                },
                            }}
                            disabled={
                                sharedImage !== null ||
                                (croppedImages[currentQuestionIndex]?.question_images?.length || 1) > 1
                            }
                        >
                            Shared Content (Text)
                        </Button>
                        <Button
                            onClick={() => handleUseAsSharedImage(currentQuestionIndex, 'diagram')}
                            variant="outlined"
                            sx={{
                                flex: 1,
                                color: colors.greenAccent[300],
                                borderColor: colors.greenAccent[400],
                                '&:hover': {
                                    borderColor: colors.greenAccent[300],
                                    backgroundColor: `${colors.greenAccent[400]}22`,
                                },
                                '&.Mui-disabled': {
                                    color: colors.grey[500],
                                    borderColor: colors.grey[600],
                                },
                            }}
                            disabled={
                                sharedImage !== null ||
                                (croppedImages[currentQuestionIndex]?.question_images?.length || 1) > 1
                            }
                        >
                            Shared Diagram (Image)
                        </Button>
                    </Box>

                    {/* Finish */}
                    <Button
                        onClick={handleFinishQuestion}
                        color="primary"
                        variant="contained"
                        fullWidth
                    >
                        Done - Start New Question
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Parent Selection Dialog */}
            <Dialog
                open={showParentSelectionDialog}
                onClose={handleCancelParentSelection}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Select Parent Question</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Select which question this is a follow-up/sub-part of. The parent question will be
                        solved first, and its answer will be used as context when solving this child question.
                    </DialogContentText>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        {croppedImages.map((cropItem, index) => {
                            // Don't show: current question, questions that are already children, or questions after this one
                            if (index === pendingChildIndex ||
                                (cropItem.parent_index !== null && cropItem.parent_index !== undefined) ||
                                index >= pendingChildIndex) {
                                return null;
                            }

                            return (
                                <Box
                                    key={index}
                                    onClick={() => handleSelectParent(index)}
                                    sx={{
                                        cursor: 'pointer',
                                        border: `2px solid ${colors.primary[400]}`,
                                        borderRadius: '8px',
                                        padding: '12px',
                                        width: '200px',
                                        '&:hover': {
                                            borderColor: colors.greenAccent[500],
                                            backgroundColor: colors.primary[700],
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            Question {index + 1}
                                        </Typography>
                                        {cropItem.is_parent && (
                                            <Chip
                                                label="Parent"
                                                size="small"
                                                color="success"
                                                sx={{ height: 18, fontSize: '0.65rem' }}
                                            />
                                        )}
                                    </Box>
                                    <img
                                        src={`data:image/png;base64,${cropItem.question_images?.[0] || cropItem.question_image}`}
                                        alt={`Question ${index + 1}`}
                                        style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                                    />
                                    <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                                        Page {cropItem.page}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                    {croppedImages.filter((item, idx) =>
                        idx !== pendingChildIndex &&
                        (item.parent_index === null || item.parent_index === undefined) &&
                        idx < pendingChildIndex
                    ).length === 0 && (
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                            No valid parent questions available. Parent questions must be cropped before child questions.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelParentSelection} color="primary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HwQuestionCropTool;
